"use client";
import React, { createContext, useContext, useState, useEffect } from 'react';

const BlogContext = createContext();

export const useBlog = () => useContext(BlogContext);

// Sample blog data - in a real app, this would come from an API
const sampleBlogPosts = [
  {
    id: 1,
    title: "5 Creative Ways to Use Custom Fridge Magnets",
    slug: "creative-ways-custom-fridge-magnets",
    excerpt: "Custom fridge magnets aren't just for holding notes — they can showcase memories, promote businesses, and add personality to your kitchen. From family photos to brand promotions, here are five creative ideas to inspire you.",
    content: `
      <p>Custom fridge magnets have evolved far beyond simple note-holders. They've become versatile tools for self-expression, business promotion, and home decoration. Here are five creative ways to make the most of these small but mighty accessories.</p>

      <h3>1. Memory Lane Gallery</h3>
      <p>Transform your refrigerator into a rotating photo gallery. Use custom magnets featuring your favorite family photos, vacation snapshots, or milestone moments. Unlike traditional photo frames, magnetic displays can be easily rearranged and updated as new memories are made.</p>

      <h3>2. Business Card Alternatives</h3>
      <p>For small business owners, custom magnets serve as memorable business cards that customers actually keep. Include your logo, contact information, and a brief service description. Every time someone opens their fridge, they'll see your brand.</p>

      <h3>3. Educational Tools for Kids</h3>
      <p>Create learning magnets featuring letters, numbers, or educational facts. These interactive tools make learning fun while keeping important information visible. Perfect for homeschooling families or parents who want to reinforce lessons at home.</p>

      <h3>4. Event Invitations and Save-the-Dates</h3>
      <p>Stand out from paper invitations with magnetic save-the-dates for weddings, parties, or corporate events. Guests are more likely to keep and remember magnetic invitations, ensuring better attendance rates.</p>

      <h3>5. Seasonal Decorations</h3>
      <p>Switch up your kitchen's look with seasonal magnetic decorations. Create sets for holidays, seasons, or special occasions. It's an affordable way to refresh your space without permanent changes.</p>

      <p>The beauty of custom fridge magnets lies in their versatility and practicality. They're affordable to produce, easy to distribute, and serve multiple purposes. Whether you're looking to preserve memories, promote your business, or simply add some personality to your kitchen, custom magnets offer endless possibilities.</p>
    `,
    author: "Sarah Johnson",
    publishedDate: "2026-02-20",
    category: "DIY & Crafts",
    tags: ["magnets", "customization", "home decor", "business"],
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=800&h=400&fit=crop",
    featured: true
  },
  {
    id: 2,
    title: "How to Design the Perfect Personalized Gift",
    slug: "design-perfect-personalized-gift",
    excerpt: "Choosing the right colors, materials, and layout makes all the difference. In this guide, we'll walk you through the basics of creating a gift that feels thoughtful, stylish, and unforgettable.",
    content: `
      <p>Creating the perfect personalized gift requires more than just adding a name to an item. It's about understanding the recipient, choosing the right design elements, and ensuring the final product reflects both thoughtfulness and quality.</p>

      <h3>Understanding Your Recipient</h3>
      <p>Before diving into design choices, consider the person you're creating for. What are their interests, favorite colors, and personal style? A gift for a minimalist will look very different from one for someone who loves bold, vibrant designs.</p>

      <h3>Color Psychology in Gift Design</h3>
      <p>Colors evoke emotions and memories. Blue conveys trust and calm, red suggests energy and passion, while green represents growth and harmony. Choose colors that align with the recipient's personality or the message you want to convey.</p>

      <h3>Typography Matters</h3>
      <p>The font you choose can dramatically impact the gift's feel. Script fonts feel elegant and personal, while sans-serif fonts appear modern and clean. Consider the occasion and recipient when selecting typography.</p>

      <h3>Material Selection</h3>
      <p>Different materials convey different messages. Wood feels warm and natural, metal appears sleek and modern, while fabric suggests comfort and coziness. Choose materials that match both the recipient's taste and the gift's intended use.</p>

      <h3>Layout and Composition</h3>
      <p>Good design follows principles of balance, contrast, and hierarchy. Ensure your text is readable, images are high-quality, and the overall composition feels harmonious. White space is your friend – don't overcrowd the design.</p>

      <h3>Adding Personal Touches</h3>
      <p>The most memorable personalized gifts include unexpected details. Consider adding inside jokes, meaningful dates, or subtle references that only the recipient will understand. These small touches transform a nice gift into a treasured keepsake.</p>

      <p>Remember, the best personalized gifts tell a story. They reflect not just the recipient's personality, but also the thought and care you put into creating something unique just for them.</p>
    `,
    author: "Michael Chen",
    publishedDate: "2026-02-10",
    category: "Design Tips",
    tags: ["design", "personalization", "gifts", "typography"],
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=800&h=400&fit=crop",
    featured: true
  },
  {
    id: 3,
    title: "Why Custom Branding Boosts Small Businesses",
    slug: "custom-branding-boosts-small-businesses",
    excerpt: "Branded merchandise strengthens recognition and builds trust. Discover how personalized prints and giveaways can help your business stand out in a competitive market.",
    content: `
      <p>In today's competitive marketplace, small businesses need every advantage they can get. Custom branding through personalized merchandise isn't just about looking professional – it's a powerful marketing tool that can significantly impact your business growth.</p>

      <h3>Building Brand Recognition</h3>
      <p>Consistent visual branding across all touchpoints helps customers remember your business. When your logo appears on business cards, promotional items, and packaging, it reinforces your brand identity and makes your business more memorable.</p>

      <h3>Creating Professional Credibility</h3>
      <p>Custom branded materials signal that you're serious about your business. Professional-looking business cards, branded packaging, and promotional items help establish trust with potential customers and partners.</p>

      <h3>Cost-Effective Marketing</h3>
      <p>Branded merchandise offers exceptional return on investment. A single branded item can generate hundreds of impressions over its lifetime, making it one of the most cost-effective marketing strategies available to small businesses.</p>

      <h3>Customer Loyalty and Retention</h3>
      <p>Giving customers branded items creates a sense of connection to your business. People love free, useful items, and when they use your branded merchandise, they're reminded of your business regularly.</p>

      <h3>Word-of-Mouth Marketing</h3>
      <p>Quality branded items often get shared or noticed by others, creating organic marketing opportunities. A well-designed t-shirt or useful promotional item can spark conversations about your business.</p>

      <h3>Choosing the Right Items</h3>
      <p>Select promotional items that align with your brand and provide value to recipients. Useful items like pens, notebooks, or tech accessories tend to be kept longer and used more frequently than novelty items.</p>

      <h3>Measuring Success</h3>
      <p>Track the impact of your branded merchandise through customer surveys, sales data, and brand awareness metrics. This helps you understand which items and strategies work best for your business.</p>

      <p>Custom branding isn't just an expense – it's an investment in your business's future. When done thoughtfully, branded merchandise can help small businesses compete with larger companies and build lasting customer relationships.</p>
    `,
    author: "Lisa Rodriguez",
    publishedDate: "2026-01-28",
    category: "Business",
    tags: ["branding", "marketing", "small business", "promotional items"],
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=400&fit=crop",
    featured: false
  },
  {
    id: 4,
    title: "The Psychology of Color in Custom Printing",
    slug: "psychology-color-custom-printing",
    excerpt: "Colors influence emotions and decisions more than we realize. Learn how to choose the right colors for your custom prints to create the desired impact and connect with your audience.",
    content: `
      <p>Color is one of the most powerful tools in design, capable of evoking emotions, influencing decisions, and creating lasting impressions. Understanding color psychology can help you make more effective choices for your custom printing projects.</p>

      <h3>The Science Behind Color Psychology</h3>
      <p>Colors affect us on both conscious and subconscious levels. They can trigger memories, influence mood, and even impact purchasing decisions. This psychological response to color is rooted in both biology and cultural associations.</p>

      <h3>Red: Energy and Urgency</h3>
      <p>Red is associated with passion, energy, and urgency. It's attention-grabbing and can increase heart rate. Use red for call-to-action elements, sale announcements, or when you want to create excitement. However, use it sparingly as it can be overwhelming.</p>

      <h3>Blue: Trust and Professionalism</h3>
      <p>Blue conveys trust, stability, and professionalism. It's the most popular color in corporate branding because it suggests reliability and competence. Different shades of blue can evoke different feelings – navy suggests authority, while light blue feels calming.</p>

      <h3>Green: Growth and Harmony</h3>
      <p>Green represents nature, growth, and harmony. It's associated with health, wealth, and environmental consciousness. Green is easy on the eyes and creates a sense of balance, making it ideal for wellness brands or eco-friendly products.</p>

      <h3>Yellow: Optimism and Creativity</h3>
      <p>Yellow is the color of sunshine and happiness. It stimulates mental activity and generates muscle energy. However, it can be straining to the eye in large amounts, so use it as an accent color rather than a dominant one.</p>

      <h3>Purple: Luxury and Creativity</h3>
      <p>Purple has long been associated with royalty, luxury, and creativity. It suggests sophistication and can add an element of mystery to your designs. Lighter purples feel more romantic, while darker purples convey luxury.</p>

      <h3>Cultural Considerations</h3>
      <p>Remember that color meanings can vary across cultures. While white represents purity in Western cultures, it's associated with mourning in some Eastern cultures. Consider your audience when making color choices.</p>

      <h3>Practical Application Tips</h3>
      <p>Use the 60-30-10 rule: 60% dominant color, 30% secondary color, and 10% accent color. This creates visual balance and prevents color overload. Also, consider color contrast for readability and accessibility.</p>

      <p>By understanding color psychology, you can create custom prints that not only look beautiful but also communicate your intended message effectively and connect with your audience on an emotional level.</p>
    `,
    author: "David Kim",
    publishedDate: "2026-01-15",
    category: "Design Tips",
    tags: ["color psychology", "design", "printing", "branding"],
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1541701494587-cb58502866ab?w=800&h=400&fit=crop",
    featured: false
  },
  {
    id: 5,
    title: "Sustainable Printing: Eco-Friendly Options for Your Projects",
    slug: "sustainable-printing-eco-friendly-options",
    excerpt: "Environmental consciousness is more important than ever. Discover sustainable printing options that reduce environmental impact without compromising on quality or design.",
    content: `
      <p>As environmental awareness grows, businesses and individuals are seeking ways to reduce their ecological footprint. Sustainable printing practices offer an excellent opportunity to make a positive environmental impact while maintaining high-quality results.</p>

      <h3>Understanding Sustainable Printing</h3>
      <p>Sustainable printing involves using environmentally friendly materials, processes, and practices throughout the printing lifecycle. This includes everything from paper sourcing to ink composition to waste management.</p>

      <h3>Eco-Friendly Paper Options</h3>
      <p>Choose papers made from recycled content, sustainably managed forests, or alternative fibers like bamboo or hemp. Look for certifications like FSC (Forest Stewardship Council) or PEFC (Programme for the Endorsement of Forest Certification).</p>

      <h3>Sustainable Ink Choices</h3>
      <p>Vegetable-based inks, particularly soy ink, are excellent alternatives to petroleum-based inks. They produce vibrant colors, are easier to remove during recycling, and have lower volatile organic compound (VOC) emissions.</p>

      <h3>Digital vs. Offset Printing</h3>
      <p>Digital printing often has a smaller environmental footprint for small runs because it eliminates the need for printing plates and reduces setup waste. However, offset printing can be more efficient for large quantities.</p>

      <h3>Reducing Waste</h3>
      <p>Minimize waste by accurately calculating print quantities, using efficient layouts to maximize paper usage, and implementing proper recycling programs for printing waste and unused materials.</p>

      <h3>Local Printing Benefits</h3>
      <p>Working with local printers reduces transportation emissions and supports your local economy. Many local printers are also more willing to work with you on sustainable practices and custom solutions.</p>

      <h3>Designing for Sustainability</h3>
      <p>Design choices can significantly impact environmental footprint. Use fewer colors, avoid heavy ink coverage, and design for standard paper sizes to minimize waste. Consider the entire lifecycle of your printed materials.</p>

      <h3>Communicating Your Commitment</h3>
      <p>Don't forget to communicate your sustainable printing choices to your audience. Many consumers appreciate and support businesses that demonstrate environmental responsibility.</p>

      <p>Sustainable printing doesn't mean compromising on quality or design. With the right choices and practices, you can create beautiful, effective printed materials while minimizing your environmental impact and appealing to environmentally conscious consumers.</p>
    `,
    author: "Emma Thompson",
    publishedDate: "2026-01-05",
    category: "Sustainability",
    tags: ["sustainability", "eco-friendly", "printing", "environment"],
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=800&h=400&fit=crop",
    featured: false
  }
];

export const BlogProvider = ({ children }) => {
  const [blogPosts, setBlogPosts] = useState(sampleBlogPosts);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Get unique categories
  const categories = ['all', ...new Set(blogPosts.map(post => post.category))];

  // Filter posts based on search and category
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = searchTerm === '' ||
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Get featured posts
  const featuredPosts = blogPosts.filter(post => post.featured);

  // Get post by slug
  const getPostBySlug = (slug) => {
    return blogPosts.find(post => post.slug === slug);
  };

  // Get related posts (same category, excluding current post)
  const getRelatedPosts = (currentPost, limit = 3) => {
    return blogPosts
      .filter(post => post.id !== currentPost.id && post.category === currentPost.category)
      .slice(0, limit);
  };

  const value = {
    blogPosts,
    filteredPosts,
    featuredPosts,
    loading,
    searchTerm,
    setSearchTerm,
    selectedCategory,
    setSelectedCategory,
    categories,
    getPostBySlug,
    getRelatedPosts
  };

  return (
    <BlogContext.Provider value={value}>
      {children}
    </BlogContext.Provider>
  );
};
